import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { spaces, palette } from '../../assets/styles/themes/tokens';
import OwnCollectionOverviewWatchlistEmptyState from './OwnCollectionOverviewWatchlistEmptyState';
import OwnCollectionOverviewWatchlistItemsList from './OwnCollectionOverviewWatchlistItemsList';
import { usePinnedStatus } from '../../reducers/pinned.selectors';
import { AsyncActionStatus } from '../../actions/common.actionTypes';
import { usePinnedContents } from '../../reducers/pinned.selectors';
import { Collection, ContentItem } from '../../types/AppContent';
import useCollectionActions from '../../utils/useCollectionActions';

interface OwnCollectionOverviewWatchlistProps {
  collection: Collection;
}
const OwnCollectionOverviewWatchlist = ({ collection }: OwnCollectionOverviewWatchlistProps) => {
  const { t } = useTranslation();
  const pinnedStatus = usePinnedStatus();
  const pinnedCollections: ContentItem[] = usePinnedContents();
  const { addItem, removeItem } = useCollectionActions(collection.id);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    const ids = new Set<string>();
    pinnedCollections.forEach(item => {
      const isItemInCollection = collection.contents?.some(c => c.id === item.id);
      if (isItemInCollection) {
        ids.add(item.id);
      }
    });
    return ids;
  });

  const handleItemClick = useCallback((item: ContentItem) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
        removeItem(item);
      } else {
        next.add(item.id);
        addItem(item);
      }
      return next;
    });
  }, [addItem, removeItem]);

  if (pinnedStatus === AsyncActionStatus.LOADING) {
    return null;
  }

  return (
    <Box sx={{
      height: 'auto',
      padding: `${spaces.lg} ${spaces.md}`,
      backgroundColor: palette.background.paperElevation2,
      border: `1px solid ${palette.elevation.outlined}`,
    }}>
      <Box sx={{
        paddingTop: spaces.md,
        paddingBottom: spaces.sm,
        marginBottom: spaces.md,
        textAlign: 'center',
      }}>
        <Typography
          variant='overline'
          sx={{
            textTransform: 'uppercase',
          }}
        >
          {t('Add to watchlist')}
        </Typography>
      </Box>
      {pinnedCollections?.length === 0 ? <OwnCollectionOverviewWatchlistEmptyState /> :
        <OwnCollectionOverviewWatchlistItemsList
          pinnedCollections={pinnedCollections}
          selectedIds={selectedIds}
          getItemClickHandler={(item) => () => handleItemClick(item)}
        />
      }
    </Box>
  );
};

export default OwnCollectionOverviewWatchlist;
